import { useNavigate, useParams } from "react-router-dom";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useCallback, useMemo } from "react";
import flatMapDeep from "lodash/flatMapDeep";
import { useDisclosure } from "@chakra-ui/react";
import toast from "react-hot-toast";
import { useFetchContestDetail } from "./useFetchContestDetail";
import { useFetchMySeriesList } from "./useFetchMySeriesList";
import {
  useGetEntrySeriesIds,
  useUpdateSeriesId,
} from "@/state/entry-series-state";
import { useMutateLegalAgreement } from "./useMutateLegalAgreement";
import { useMutateContestEntry } from "./useMutateContestEntry";
import { hasEntryDiff } from "./entry-diff-util";

export const useContainer = () => {
  const { contestId } = useParams<{ contestId: string }>();

  const seriesIds = useGetEntrySeriesIds();
  const { setInitialEntries } = useUpdateSeriesId();
  const selectedItemCount = seriesIds.length;
  const navigate = useNavigate();

  // コンテスト情報取得
  const { data: contestDetail } = useFetchContestDetail({
    contestId,
    config: {
      onSuccess: (data) => {
        setInitialEntries(data.userEntrySeriesIds);
      },
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    },
  });

  // 一覧表示
  const { data, isLoading, error, size, setSize } = useFetchMySeriesList();

  const showEmptyData =
    !isLoading &&
    !error &&
    data &&
    data.length > 0 &&
    data[0]?.items.length === 0;

  const items = useMemo(() => {
    return flatMapDeep(data?.map((page) => page.items) ?? []);
  }, [data]);

  const hasNext = useMemo(() => {
    return data?.[data.length - 1]?.hasNext ?? false;
  }, [data]);

  const onLoadNext = useCallback(() => {
    const lastData = data?.[data.length - 1];
    if (!lastData || !lastData.hasNext) return;

    setSize(size + 1);
  }, [data, setSize, size]);

  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasNext,
    onLoadMore: onLoadNext,
  });

  // 応募
  const { trigger: mutateLegalAgreement } = useMutateLegalAgreement();
  const { trigger: mutateContestEntry } = useMutateContestEntry();

  const legalAgreementDisclosure = useDisclosure();

  const handleEntry = useCallback(async () => {
    if (!contestDetail) return;

    try {
      await mutateLegalAgreement({
        legalAgreement: contestDetail.legalAgreement,
      });
    } catch (err) {
      console.error(err);
      toast.error("応募規約の同意に失敗しました");

      return;
    }

    try {
      await mutateLegalAgreement({
        legalAgreement: contestDetail.legalAgreement,
      });

      const result = await mutateContestEntry({
        contestId: contestDetail.id,
        enteredSeriesIds: seriesIds,
      });

      setInitialEntries(seriesIds);

      switch (result) {
        case "SUCCEEDED": {
          const previousSeriesIds = contestDetail.userEntrySeriesIds;
          const successMessage =
            previousSeriesIds.length === 0
              ? "コンテストの応募が完了しました"
              : selectedItemCount === 0
                ? "応募を取り消しました"
                : "応募内容を更新しました";

          toast.success(successMessage);
          legalAgreementDisclosure.onClose();
          navigate(`/contests`);
          break;
        }
        case "FAILED_CONTEST_NOT_OPENED": {
          toast.error("コンテストが開催されていません");
          break;
        }
        case "FAILED_REQUIRE_LEGAL_AGREEMENT_ACCEPTANCE": {
          toast.error("最新の応募規約への同意が必要です");
          break;
        }
        case "FAILED_TOO_MANY_ENTRIES": {
          toast.error("応募数が上限に達しています");
          break;
        }
        default: {
          toast.error("応募に失敗しました。");
          break;
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("応募に失敗しました。");
    }
  }, [
    contestDetail,
    legalAgreementDisclosure,
    mutateContestEntry,
    mutateLegalAgreement,
    navigate,
    selectedItemCount,
    seriesIds,
    setInitialEntries,
  ]);

  const submitEnabled = useMemo(() => {
    if (contestDetail === undefined) return false;

    return hasEntryDiff(seriesIds, contestDetail.userEntrySeriesIds);
  }, [contestDetail, seriesIds]);

  const submitButtonColor = useMemo(() => {
    if (contestDetail === undefined) return "contentLink";
    const previousSeriesIds = contestDetail.userEntrySeriesIds;
    if (previousSeriesIds.length === 0) {
      return "contentLink";
    }

    return selectedItemCount === 0 ? "contentNegative" : "contentLink";
  }, [contestDetail, selectedItemCount]);

  const submitButtonText = useMemo(() => {
    if (contestDetail === undefined) return "選択した作品を応募する";
    const previousSeriesIds = contestDetail.userEntrySeriesIds;

    if (previousSeriesIds.length === 0) {
      return `選択した作品を応募する (${selectedItemCount})`;
    }

    return selectedItemCount === 0
      ? "応募を取り消す"
      : `応募する作品を変更する (${selectedItemCount})`;
  }, [contestDetail, selectedItemCount]);

  const handleSubmit = useCallback(() => {
    if (!submitEnabled || !contestDetail) {
      return;
    }

    if (!contestDetail.legalAgreement.latestVersion.userAccepted) {
      legalAgreementDisclosure.onOpen();
      return;
    }

    handleEntry();
  }, [contestDetail, handleEntry, legalAgreementDisclosure, submitEnabled]);

  const handleOnSubmitForLegalAgreement = useCallback(() => {
    handleEntry();
  }, [handleEntry]);

  return {
    contestDetail,
    items,
    isLoading,
    showEmptyData,
    infiniteRef,
    hasNext,
    submitEnabled,
    submitButtonColor,
    submitButtonText,
    legalAgreementDisclosure,
    handleSubmit,
    handleOnSubmitForLegalAgreement,
  };
};
